import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { Box, Typography, Paper } from '@mui/material';
import { Block } from '@mui/icons-material';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [disabledMessage, setDisabledMessage] = useState('');

  useEffect(() => {
    const token = Cookies.get('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user`);
      if (response.data) {
        setUser(response.data);
      } else {
        console.error('No user data received from server');
        logout(); // Clear invalid session
      }
    } catch (error) {
      console.error('Auth check failed:', error.response?.data || error.message);
      // Check if user is disabled
      if (error.response?.status === 403 && error.response?.data?.disabled) {
        // User is disabled, show message and logout
        setDisabledMessage(error.response.data.message || 'Your account has been temporarily disabled');
        setIsDisabled(true);
        logout();
      } else {
        setIsDisabled(false);
        logout(); // Clear invalid session
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (name, password) => {
    try {
      // Clear caches and local storage before login
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear service worker caches if available
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => caches.delete(cacheName))
          );
        } catch (cacheError) {
          console.warn('Failed to clear caches:', cacheError);
        }
      }
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        name,
        password
      });
      
      const { token, user: userData } = response.data;
      
      // Set cookie with token that expires in 30 days
      Cookies.set('token', token, { expires: 30 });
      Cookies.set('userid', userData.id, { expires: 30 });
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Refetch full user data to ensure all fields (like master) are populated
      try {
        const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user`);
        if (userResponse.data) {
          setUser(userResponse.data);
        } else {
          // Fallback to login response data if refetch fails
          setUser(userData);
        }
      } catch (userError) {
        console.warn('Failed to refetch user data after login, using login response data:', userError);
        // Fallback to login response data
        setUser(userData);
      }
    } catch (error) {
      // Handle disabled user error
      if (error.response?.status === 403) {
        throw new Error(error.response.data.message || 'Your account has been temporarily disabled');
      }
      throw error;
    }
  };

  const register = async (name, password) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/register`, {
      name,
      password
    });
    
    const { token, user: userData } = response.data;
    
    // Set cookie with token that expires in 30 days
    Cookies.set('token', token, { expires: 30 });
    Cookies.set('userid', userData.id, { expires: 30 });
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Refetch full user data to ensure all fields (like master) are populated
    try {
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/auth/user`);
      if (userResponse.data) {
        setUser(userResponse.data);
      } else {
        // Fallback to register response data if refetch fails
        setUser(userData);
      }
    } catch (userError) {
      console.warn('Failed to refetch user data after register, using register response data:', userError);
      // Fallback to register response data
      setUser(userData);
    }
  };

  const logout = () => {
    Cookies.remove('token');
    Cookies.remove('userid');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsDisabled(false);
    setDisabledMessage('');
  };

  // Show disabled message overlay if user is disabled
  if (isDisabled) {
    return (
      <AuthContext.Provider value={{ user, login, register, logout, loading }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 3
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              maxWidth: 600,
              width: '100%'
            }}
          >
            <Block
              sx={{
                fontSize: 80,
                color: 'error.main',
                mb: 3
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                mb: 2,
                color: 'text.primary'
              }}
            >
              Account Disabled
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: 'text.secondary',
                mb: 4
              }}
            >
              {disabledMessage}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary'
              }}
            >
              Please contact your administrator if you believe this is an error.
            </Typography>
          </Paper>
        </Box>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 